import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Checkbox,
  FormControlLabel,
  Snackbar,
} from '@mui/material';
import { Edit, Delete, Add, Refresh, OpenInNew } from '@mui/icons-material';
import { regulationsApi, Regulation, RegulationCreateRequest } from '../services/api';

const RegulationsManagement: React.FC = () => {
  const [regulations, setRegulations] = useState<Regulation[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRegulation, setSelectedRegulation] = useState<Regulation | null>(null);
  const [formData, setFormData] = useState<RegulationCreateRequest>({
    regulation_code: '',
    regulation_name: '',
    country: '',
    jurisdiction: '',
    status: 'active',
    is_current: true,
    priority: 1,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({
    jurisdiction: '',
    status: '',
    search: '',
  });

  const jurisdictions = [
    { value: 'us_fda', label: 'US FDA' },
    { value: 'eu_mdr', label: 'EU MDR' },
    { value: 'australia_tga', label: 'Australia TGA' },
    { value: 'canada_hc', label: 'Canada HC' },
  ];

  const fetchRegulations = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await regulationsApi.getAllRegulations({
        jurisdiction: filters.jurisdiction || undefined,
        status: filters.status || undefined,
        search: filters.search || undefined,
      });
      console.log('Regulations API response:', data);
      const filtered = (data.regulations || []).filter((reg) => reg.jurisdiction !== 'india_imdr');
      setRegulations(filtered);
      if (filtered.length === 0) {
        console.log('No regulations found in database');
      }
    } catch (err: any) {
      console.error('Error fetching regulations:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch regulations';
      setError(errorMessage);
      setRegulations([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchRegulations();
  }, [fetchRegulations]);

  const handleCreate = () => {
    setSelectedRegulation(null);
    setFormData({
      regulation_code: '',
      regulation_name: '',
      country: '',
      jurisdiction: '',
      status: 'active',
      is_current: true,
      priority: 1,
      applicable_device_classes: [],
      applicable_device_types: [],
      reference_urls: [],
      related_forms: [],
      related_checklists: [],
      key_requirements: [],
      compliance_areas: [],
      tags: [],
    });
    setCreateDialogOpen(true);
  };

  const handleEdit = (regulation: Regulation) => {
    setSelectedRegulation(regulation);
    setFormData({
      regulation_code: regulation.regulation_code,
      regulation_name: regulation.regulation_name,
      short_name: regulation.short_name,
      description: regulation.description,
      summary: regulation.summary,
      country: regulation.country,
      jurisdiction: regulation.jurisdiction,
      regulatory_authority: regulation.regulatory_authority,
      applicable_device_classes: regulation.applicable_device_classes,
      applicable_device_types: regulation.applicable_device_types,
      is_ivd_specific: regulation.is_ivd_specific,
      is_md_specific: regulation.is_md_specific,
      version: regulation.version,
      effective_date: regulation.effective_date?.split('T')[0],
      expiry_date: regulation.expiry_date?.split('T')[0],
      publication_date: regulation.publication_date?.split('T')[0],
      is_current: regulation.is_current,
      official_url: regulation.official_url,
      official_document_url: regulation.official_document_url,
      reference_urls: regulation.reference_urls,
      related_forms: regulation.related_forms,
      related_checklists: regulation.related_checklists,
      key_requirements: regulation.key_requirements,
      compliance_areas: regulation.compliance_areas,
      status: regulation.status,
      priority: regulation.priority,
      tags: regulation.tags,
    });
    setEditDialogOpen(true);
  };

  const handleDelete = (regulation: Regulation) => {
    setSelectedRegulation(regulation);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setError('');
      setSuccess('');
      
      if (selectedRegulation) {
        await regulationsApi.updateRegulation(selectedRegulation.id, formData);
        setSuccess('Regulation updated successfully!');
        setEditDialogOpen(false);
      } else {
        await regulationsApi.createRegulation(formData);
        setSuccess('Regulation created successfully!');
        setCreateDialogOpen(false);
      }
      fetchRegulations();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save regulation');
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedRegulation) return;
    
    try {
      setError('');
      await regulationsApi.deleteRegulation(selectedRegulation.id);
      setSuccess('Regulation deleted successfully!');
      setDeleteDialogOpen(false);
      fetchRegulations();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete regulation');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          component="h1"
          sx={{
            fontSize: '1.1rem',
            fontWeight: 650,
            color: '#111827',
            letterSpacing: '-0.02em',
          }}
        >
          Regulations Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchRegulations}
            sx={{
              borderRadius: 10,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8rem',
              borderColor: '#e5e7eb',
              color: '#6b7280',
              '&:hover': { borderColor: '#d1d5db', bgcolor: '#f8f9fb' },
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
            sx={{
              borderRadius: 10,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8rem',
              bgcolor: '#111827',
              color: '#fff',
              '&:hover': { bgcolor: '#1f2937' },
            }}
          >
            Add Regulation
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box
          sx={{
            p: 2,
            flex: '1 1 200px',
            minWidth: 200,
            bgcolor: '#fff',
            border: '1px solid #eceef1',
            borderRadius: 12,
          }}
        >
          <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
            Total Regulatory Authorities
          </Typography>
          <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827' }}>
            {regulations.length}
          </Typography>
        </Box>
        <Box
          sx={{
            p: 2,
            flex: '1 1 200px',
            minWidth: 200,
            bgcolor: '#fff',
            border: '1px solid #eceef1',
            borderRadius: 12,
          }}
        >
          <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
            FDA (US)
          </Typography>
          <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827' }}>
            {regulations.filter(r => r.country === 'US').length}
          </Typography>
        </Box>
        <Box
          sx={{
            p: 2,
            flex: '1 1 200px',
            minWidth: 200,
            bgcolor: '#fff',
            border: '1px solid #eceef1',
            borderRadius: 12,
          }}
        >
          <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
            European Commission (EU)
          </Typography>
          <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827' }}>
            {regulations.filter(r => r.country === 'EU').length}
          </Typography>
        </Box>
        <Box
          sx={{
            p: 2,
            flex: '1 1 200px',
            minWidth: 200,
            bgcolor: '#fff',
            border: '1px solid #eceef1',
            borderRadius: 12,
          }}
        >
          <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1 }}>
            Active Authorities
          </Typography>
          <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827' }}>
            {regulations.filter(r => r.status === 'active').length}
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          bgcolor: '#fff',
          border: '1px solid #eceef1',
          borderRadius: 12,
          boxShadow: 'none',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Search by code, name..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 10,
                  bgcolor: '#f8f9fb',
                  fontSize: '0.85rem',
                },
              }}
            />
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 10,
                  bgcolor: '#f8f9fb',
                  fontSize: '0.85rem',
                },
              }}
            >
              <InputLabel>Jurisdiction</InputLabel>
              <Select
                value={filters.jurisdiction}
                onChange={(e) => setFilters({ ...filters, jurisdiction: e.target.value })}
                label="Jurisdiction"
              >
                <MenuItem value="">All</MenuItem>
                {jurisdictions.map((j) => (
                  <MenuItem key={j.value} value={j.value}>
                    {j.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 10,
                  bgcolor: '#f8f9fb',
                  fontSize: '0.85rem',
                },
              }}
            >
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="superseded">Superseded</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 'calc(100vh - 500px)',
            overflow: 'auto',
            bgcolor: '#fff',
            border: '1px solid #eceef1',
            borderRadius: 12,
            boxShadow: 'none',
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    bgcolor: '#f8f9fb',
                    color: '#6b7280',
                    fontWeight: 600,
                    fontSize: '0.72rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    borderBottom: '1px solid #eceef1',
                  }}
                >
                  Code
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: '#f8f9fb',
                    color: '#6b7280',
                    fontWeight: 600,
                    fontSize: '0.72rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    borderBottom: '1px solid #eceef1',
                  }}
                >
                  Name
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: '#f8f9fb',
                    color: '#6b7280',
                    fontWeight: 600,
                    fontSize: '0.72rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    borderBottom: '1px solid #eceef1',
                  }}
                >
                  Country
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: '#f8f9fb',
                    color: '#6b7280',
                    fontWeight: 600,
                    fontSize: '0.72rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    borderBottom: '1px solid #eceef1',
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: '#f8f9fb',
                    color: '#6b7280',
                    fontWeight: 600,
                    fontSize: '0.72rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    borderBottom: '1px solid #eceef1',
                  }}
                >
                  Current
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: '#f8f9fb',
                    color: '#6b7280',
                    fontWeight: 600,
                    fontSize: '0.72rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    borderBottom: '1px solid #eceef1',
                  }}
                >
                  Effective Date
                </TableCell>
                <TableCell
                  sx={{
                    bgcolor: '#f8f9fb',
                    color: '#6b7280',
                    fontWeight: 600,
                    fontSize: '0.72rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    borderBottom: '1px solid #eceef1',
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {regulations.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    align="center"
                    sx={{
                      py: 4,
                      fontSize: '0.82rem',
                      color: '#374151',
                      borderBottom: '1px solid #f3f4f6',
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body1" color="text.secondary">
                        {error ? (
                          <>
                            <Alert severity="error" sx={{ mb: 2, borderRadius: 10 }}>
                              {error}
                            </Alert>
                            <Typography variant="body2">
                              Please check your database connection and ensure the regulations table exists.
                            </Typography>
                          </>
                        ) : (
                          <>
                            No regulations found in the database.
                            <br />
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              Click "Add Regulation" to create your first regulation.
                            </Typography>
                          </>
                        )}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                regulations.map((regulation) => (
                  <TableRow
                    key={regulation.id}
                    sx={{
                      '&:hover': { bgcolor: '#f8f9fb' },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontSize: '0.82rem',
                        color: '#374151',
                        borderBottom: '1px solid #f3f4f6',
                      }}
                    >
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.82rem', color: '#374151' }}>
                        {regulation.regulation_code}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: '0.82rem',
                        color: '#374151',
                        borderBottom: '1px solid #f3f4f6',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#374151' }}>
                        {regulation.regulation_name}
                      </Typography>
                      {regulation.short_name && (
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                          ({regulation.short_name})
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: '0.82rem',
                        color: '#374151',
                        borderBottom: '1px solid #f3f4f6',
                      }}
                    >
                      <Chip
                        label={regulation.country}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          borderRadius: 6,
                          borderColor: '#e5e7eb',
                          color: '#6b7280',
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: '0.82rem',
                        color: '#374151',
                        borderBottom: '1px solid #f3f4f6',
                      }}
                    >
                      <Chip
                        label={regulation.status}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          borderRadius: 6,
                          ...(regulation.status === 'active'
                            ? { bgcolor: '#dcfce7', color: '#166534' }
                            : { bgcolor: '#fef2f2', color: '#991b1b' }),
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: '0.82rem',
                        color: '#374151',
                        borderBottom: '1px solid #f3f4f6',
                      }}
                    >
                      {regulation.is_current ? (
                        <Chip
                          label="Yes"
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.7rem',
                            borderRadius: 6,
                            bgcolor: '#dcfce7',
                            color: '#166534',
                          }}
                        />
                      ) : (
                        <Chip
                          label="No"
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.7rem',
                            borderRadius: 6,
                            bgcolor: '#fef2f2',
                            color: '#991b1b',
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: '0.82rem',
                        color: '#374151',
                        borderBottom: '1px solid #f3f4f6',
                      }}
                    >
                      {formatDate(regulation.effective_date)}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: '0.82rem',
                        color: '#374151',
                        borderBottom: '1px solid #f3f4f6',
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(regulation)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(regulation)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {regulation.official_url && (
                          <Tooltip title="View Official URL">
                            <IconButton
                              size="small"
                              href={regulation.official_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <OpenInNew fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={createDialogOpen || editDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setEditDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 16,
            boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1rem',
            fontWeight: 650,
            color: '#111827',
          }}
        >
          {selectedRegulation ? 'Edit Regulation' : 'Create New Regulation'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              fullWidth
              label="Regulation Code *"
              value={formData.regulation_code}
              onChange={(e) => setFormData({ ...formData, regulation_code: e.target.value })}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 10,
                  bgcolor: '#f8f9fb',
                },
              }}
            />
            <TextField
              fullWidth
              label="Regulation Name *"
              value={formData.regulation_name}
              onChange={(e) => setFormData({ ...formData, regulation_name: e.target.value })}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 10,
                  bgcolor: '#f8f9fb',
                },
              }}
            />
            <TextField
              fullWidth
              label="Short Name"
              value={formData.short_name || ''}
              onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 10,
                  bgcolor: '#f8f9fb',
                },
              }}
            />
            <FormControl
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 10,
                  bgcolor: '#f8f9fb',
                },
              }}
            >
              <InputLabel>Country *</InputLabel>
              <Select
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                label="Country *"
                required
              >
                <MenuItem value="US">United States (US)</MenuItem>
                <MenuItem value="EU">European Union (EU)</MenuItem>
                <MenuItem value="IN">India (IN)</MenuItem>
              </Select>
            </FormControl>
            <FormControl
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 10,
                  bgcolor: '#f8f9fb',
                },
              }}
            >
              <InputLabel>Jurisdiction *</InputLabel>
              <Select
                value={formData.jurisdiction}
                onChange={(e) => setFormData({ ...formData, jurisdiction: e.target.value })}
                label="Jurisdiction *"
                required
              >
                <MenuItem value="us_fda">US FDA</MenuItem>
                <MenuItem value="eu_mdr">EU MDR</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Regulatory Authority"
              value={formData.regulatory_authority || ''}
              onChange={(e) => setFormData({ ...formData, regulatory_authority: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 10,
                  bgcolor: '#f8f9fb',
                },
              }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 10,
                  bgcolor: '#f8f9fb',
                },
              }}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Summary"
              value={formData.summary || ''}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 10,
                  bgcolor: '#f8f9fb',
                },
              }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                type="date"
                label="Effective Date"
                value={formData.effective_date || ''}
                onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 10,
                    bgcolor: '#f8f9fb',
                  },
                }}
              />
              <TextField
                fullWidth
                type="date"
                label="Expiry Date"
                value={formData.expiry_date || ''}
                onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 10,
                    bgcolor: '#f8f9fb',
                  },
                }}
              />
            </Box>
            <TextField
              fullWidth
              label="Official URL"
              value={formData.official_url || ''}
              onChange={(e) => setFormData({ ...formData, official_url: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 10,
                  bgcolor: '#f8f9fb',
                },
              }}
            />
            <FormControl
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 10,
                  bgcolor: '#f8f9fb',
                },
              }}
            >
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status || 'active'}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="superseded">Superseded</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_current || false}
                    onChange={(e) => setFormData({ ...formData, is_current: e.target.checked })}
                  />
                }
                label="Is Current Version"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_ivd_specific || false}
                    onChange={(e) => setFormData({ ...formData, is_ivd_specific: e.target.checked })}
                  />
                }
                label="IVD Specific"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_md_specific || false}
                    onChange={(e) => setFormData({ ...formData, is_md_specific: e.target.checked })}
                  />
                }
                label="MD Specific"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              setEditDialogOpen(false);
            }}
            sx={{
              borderRadius: 10,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8rem',
              color: '#6b7280',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              borderRadius: 10,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8rem',
              bgcolor: '#111827',
              color: '#fff',
              '&:hover': { bgcolor: '#1f2937' },
            }}
          >
            {selectedRegulation ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 16,
            boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1rem',
            fontWeight: 650,
            color: '#111827',
          }}
        >
          Delete Regulation
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#374151' }}>
            Are you sure you want to delete "{selectedRegulation?.regulation_name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              borderRadius: 10,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8rem',
              color: '#6b7280',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              borderRadius: 10,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8rem',
              bgcolor: '#111827',
              color: '#fff',
              '&:hover': { bgcolor: '#1f2937' },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: 10 }}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
      >
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ borderRadius: 10 }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RegulationsManagement;

